/**
 * RiskBadge — Reusable risk score indicator
 *
 * Displays a color-coded risk level badge with optional score value.
 * Used in Threat Analysis and Track Investigation tabs.
 */

import clsx from 'clsx';

interface RiskBadgeProps {
  score: number; // 0-100
  showScore?: boolean;
  size?: 'sm' | 'md';
}

export function getRiskConfig(score: number) {
  if (score > 80)
    return {
      bg: 'bg-red-500/20',
      text: 'text-red-400',
      border: 'border-red-500/40',
      label: 'CRITICAL',
      color: '#e63b4d',
    };
  if (score > 60)
    return {
      bg: 'bg-orange-500/20',
      text: 'text-orange-400',
      border: 'border-orange-500/40',
      label: 'HIGH',
      color: '#ff9f43',
    };
  if (score > 30)
    return {
      bg: 'bg-yellow-500/20',
      text: 'text-yellow-400',
      border: 'border-yellow-500/40',
      label: 'MODERATE',
      color: '#FFD700',
    };
  return {
    bg: 'bg-emerald-500/20',
    text: 'text-emerald-400',
    border: 'border-emerald-500/40',
    label: 'LOW',
    color: '#ffb547',
  };
}

export default function RiskBadge({
  score,
  showScore = true,
  size = 'sm',
}: RiskBadgeProps) {
  const config = getRiskConfig(score);

  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1 rounded-full border font-semibold',
        config.bg,
        config.text,
        config.border,
        size === 'sm' ? 'px-2 py-0.5 text-[10px]' : 'px-3 py-1 text-xs'
      )}
    >
      {showScore && <span className="tabular-nums">{Math.round(score)}</span>}
      <span>{config.label}</span>
    </span>
  );
}

/**
 * RiskGauge — SVG circular gauge for displaying risk score
 */
export function RiskGauge({ score }: { score: number }) {
  const config = getRiskConfig(score);
  const radius = 54;
  const circumference = 2 * Math.PI * radius;
  const progress = (Math.min(score, 100) / 100) * circumference;
  const dashOffset = circumference - progress;

  return (
    <div className="flex flex-col items-center gap-2">
      <div className="relative w-36 h-36">
        <svg className="w-full h-full -rotate-90" viewBox="0 0 120 120">
          {/* Background circle */}
          <circle
            cx="60"
            cy="60"
            r={radius}
            fill="none"
            stroke="#2c4567"
            strokeWidth="8"
          />
          {/* Progress arc */}
          <circle
            cx="60"
            cy="60"
            r={radius}
            fill="none"
            stroke={config.color}
            strokeWidth="8"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={dashOffset}
            className="transition-all duration-1000 ease-out"
          />
        </svg>
        {/* Center text */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className={clsx('text-3xl font-display font-bold tabular-nums', config.text)}>
            {Math.round(score)}
          </span>
          <span className="text-[10px] text-gray-500 uppercase tracking-wider">
            Risk Score
          </span>
        </div>
      </div>
      <span
        className={clsx(
          'px-3 py-1 rounded-full text-xs font-semibold border',
          config.bg,
          config.text,
          config.border
        )}
      >
        {config.label} RISK
      </span>
    </div>
  );
}

/**
 * RiskFactorList — Displays risk factor breakdown with colored borders
 */
export function RiskFactorList({
  factors,
}: {
  factors: { factor: string; score: number; description: string }[];
}) {
  return (
    <div className="space-y-2">
      {factors.map((f, i) => {
        const factorConfig = getRiskConfig(f.score * 2); // factors are typically 0-50 range
        return (
          <div
            key={i}
            className="flex items-start gap-3 p-3 rounded-lg bg-navy-800/50 border-l-2"
            style={{ borderLeftColor: factorConfig.color }}
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-0.5">
                <span className="text-xs font-mono text-gray-400 uppercase tracking-wide">
                  {f.factor.replace(/_/g, ' ')}
                </span>
                <span
                  className={clsx(
                    'text-xs font-bold tabular-nums',
                    factorConfig.text
                  )}
                >
                  +{Math.round(f.score)}
                </span>
              </div>
              <p className="text-sm text-gray-300">{f.description}</p>
            </div>
          </div>
        );
      })}
      {factors.length === 0 && (
        <p className="text-sm text-gray-500 text-center py-4">
          No risk factors identified
        </p>
      )}
    </div>
  );
}
